import { useRef } from "react";
import { createPortal } from "react-dom";
import { useModalA11y } from "../hooks/useModalA11y.js";

/**
 * Centered confirmation dialog. Closes on backdrop click or Escape unless
 * `busy` is set. The `tone` prop drives the confirm button colour — use
 * "danger" for destructive actions like early exits and abandoned-fee
 * triggers; default ("primary") uses the standard dark-green CTA.
 *
 * Rendered via `createPortal` into `document.body` so the backdrop's
 * blur sits above every layout stacking context — without the portal,
 * the sticky page header (z-40) ends up rendered above the backdrop
 * even at z-50 because the sticky element creates its own context.
 */
export default function ConfirmModal({
    open,
    title,
    message,
    body,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    tone = "primary",
    busy = false,
    onConfirm,
    onCancel
}) {
    // Dialog plumbing (Escape, focus trap, return-focus on close) lives in
    // a shared hook so every modal in the app behaves identically. Escape
    // is suppressed while `busy` is true so the user can't accidentally
    // dismiss mid-transaction.
    const dialogRef = useRef(null);
    useModalA11y({
        open,
        onClose: () => !busy && onCancel?.(),
        containerRef: dialogRef
    });

    if (!open) return null;

    const danger = tone === "danger";

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0E2C19]/55 backdrop-blur-sm p-4"
            onClick={() => !busy && onCancel?.()}
            role="presentation"
        >
            <div
                ref={dialogRef}
                className="w-full max-w-md overflow-hidden rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] shadow-xl"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-modal-title"
            >
                {/* Banner — danger tone gets a soft red bar with warning glyph,
                    primary tone gets the standard dark-green/gold treatment. */}
                <div className="bg-[#162e1f] border-b border-[#bfb67b]/30 px-5 py-4 flex items-center gap-3">
                    {danger ? <WarningBadge /> : <InfoBadge />}
                    <div className="min-w-0 flex-1">
                        <div className="text-[9px] font-semibold uppercase tracking-widest text-[#FBC206]">
                            {danger ? "Confirm Action" : "Confirmation"}
                        </div>
                        <h3
                            id="confirm-modal-title"
                            className="mt-0.5 font-manrope text-[15px] font-bold text-[#e6c985]"
                        >
                            {title}
                        </h3>
                    </div>
                </div>

                <div className="p-5">
                    {body ? (
                        body
                    ) : (
                        <p className="text-[12px] leading-relaxed text-white/85">
                            {message}
                        </p>
                    )}

                    <div className="mt-5 flex justify-end gap-2">
                        <button
                            className="inline-flex items-center justify-center rounded-md bg-white/10 px-4 py-2 text-[12px] font-semibold text-white ring-1 ring-[#bfb67b] hover:bg-white/20 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={onCancel}
                            disabled={busy}
                        >
                            {cancelLabel}
                        </button>
                        <button
                            className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-[12px] font-semibold transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 disabled:translate-y-0 disabled:hover:shadow-none ${
                                danger
                                    ? "bg-[#C93030] text-white"
                                    : "bg-[#FBC206] text-[#10281b]"
                            }`}
                            onClick={onConfirm}
                            disabled={busy}
                        >
                            {busy ? "Working..." : confirmLabel}
                            {!busy ? <ChevronRightSmall /> : null}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

function WarningBadge() {
    return (
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FDE2E2] ring-1 ring-[#C93030]/40">
            <svg
                className="h-5 w-5 text-[#C93030]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <path d="M12 3 L21 19 L3 19 Z" />
                <line x1="12" y1="9" x2="12" y2="14" />
                <circle cx="12" cy="17" r="0.6" fill="currentColor" />
            </svg>
        </span>
    );
}

function InfoBadge() {
    return (
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FBC206]">
            <svg
                className="h-5 w-5 text-[#1D3E29]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <circle cx="12" cy="12" r="9" />
                <line x1="12" y1="11" x2="12" y2="16" />
                <circle cx="12" cy="8" r="0.6" fill="currentColor" />
            </svg>
        </span>
    );
}

function ChevronRightSmall() {
    return (
        <svg
            className="h-3 w-3"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <polyline points="7 4 13 10 7 16" />
        </svg>
    );
}
